import {
  ActiveHoldCountsByStatus,
  CardLoginValue,
  EditNotificationPreferencesValue,
  EmailLoginValue,
  ItemRequestUsage,
  NotificationPreference,
  Patron,
  PatronAccountStatus,
  PatronCategory,
  PatronNotificationPreferences,
  PatronReferenceOptions,
  PatronRelationships,
  PatronRule,
  PatronSettingsQuickTogglesValue,
  PatronStatistics,
  RelatedPatron
} from './patron.types';
import {Pronoun, State} from '@aviary/store/common/common.types';
import {FALLBACK_BRANCH} from '@store/branches/branch.fallback';
import {ALL_HOLD_STATUSES} from '@store/holds/holds.types';
import {generateRecordForKeys} from '@aviary/store/transformation.helpers';

export const FALLBACK_PATRON_CATEGORY: PatronCategory = {
  id: 0,
  organizationId: 0,
  name: '',
  chipColor: '',
  guarantorRequired: false,
  dobRequired: false,
  boundaryRestricted: false,
  expireAtMaxAge: false,
  expirationDate: '',
  minAge: 0,
  maxAge: 0,
  registrationTerm: 0,
  registrationFee: 0,
  renewalFee: 0,
  holdFee: 0,
  overdueNotice: false,
  showLostItems: false,
  active: false,
  created: '',
  modified: ''
};

export const FALLBACK_PATRON_RULE: PatronRule = {
  id: 0,
  organizationId: 0,
  patronId: 0,
  patronCategoryId: 0,
  maxItemsOut: 0,
  maxItemsOutBorrowBlocked: false,
  maxItemsOutRenewalBlocked: false,
  maxItemsOutHoldBlocked: false,
  maxItemsOutBlockNote: '',
  maxItemsLost: 0,
  maxItemsLostBorrowBlocked: false,
  maxItemsLostRenewalBlocked: false,
  maxItemsLostHoldBlocked: false,
  maxItemsLostBlockNote: '',
  maxItemsOverdue: 0,
  maxItemsOverdueBorrowBlocked: false,
  maxItemsOverdueRenewalBlocked: false,
  maxItemsOverdueHoldBlocked: false,
  maxItemsOverdueBlockNote: '',
  maxFeeBalance: 0,
  maxFeeBalanceBorrowBlocked: false,
  maxFeeBalanceRenewalBlocked: false,
  maxFeeBalanceHoldBlocked: false,
  maxFeeBalanceBlockNote: '',
  maxHoldsPlaced: 0,
  maxPausedHoldsPlaced: 0,
  expiredCardHoldBlocked: false,
  holdsLimitOnePerCatalogPerPatron: false,
  maxHoldsNote: '',
  active: false,
  created: '',
  modified: '',

  patronCategory: FALLBACK_PATRON_CATEGORY
};

export const FALLBACK_PATRON_STATISTICS: PatronStatistics = {
  openHolds: 0,
  openLoans: 0,
  claimedReturnedCount: 0,
  heldItems: 0,
  overdueItems: 0
};

export const FALLBACK_PRONOUN: Pronoun = {
  id: 0,
  pronounsString: ''
};

export const FALLBACK_PATRON: Patron = {
  id: 0,
  googleUid: '',
  validatedVerificationUUID: '',
  pendingEmailChange: undefined,
  branchId: 0,
  defaultHoldPickupBranchId: 0,
  patronCategoryId: 0,
  barcode: '',
  email: '',
  notificationEmail: '',
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '',
  suffix: '',
  dateOfBirth: '',
  accountExpiration: '',
  phone1: '',
  phone1Ext: '',
  phone1Type: '',
  phone1Mobile: false,
  phone2: '',
  phone2Ext: '',
  phone2Mobile: false,
  phoneSms: '',
  phoneCall: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  emailVerified: false,
  pinGenerated: false,
  optInReadingHistory: false,
  oneClickHoldsEnabled: false,
  lookupAttempts: 0,
  underBalanceLimit: false,
  pronouns: FALLBACK_PRONOUN,
  category: FALLBACK_PATRON_CATEGORY,
  addresses: [],
  openHolds: 0,
  openLoans: 0,
  accountBalance: 0,
  patronRule: FALLBACK_PATRON_RULE,
  patronStatistics: FALLBACK_PATRON_STATISTICS
};

export const FALLBACK_ACTIVE_HOLD_COUNTS_BY_STATUS: ActiveHoldCountsByStatus = generateRecordForKeys(ALL_HOLD_STATUSES, 0);

export const FALLBACK_ITEM_REQUEST_USAGE: ItemRequestUsage = {
  monthlyLimit: 0,
  monthlyCount: 0
}

export const FALLBACK_PATRON_ACCOUNT_STATUS: PatronAccountStatus = {
  accountExpiration: '',
  currentAccountBalance: 0,
  depositBalance: 0,
  walletBalance: 0,
  maximumAccountBalance: 0,
  minimumBalanceDue: 0,
  activeHoldsCount: 0,
  activeHoldCountsByStatus: FALLBACK_ACTIVE_HOLD_COUNTS_BY_STATUS,
  blocks: [],
  unresolvedCheckoutCount: 0,
  unresolvedItemRequestCount: 0,
  underItemRequestLimit: false,
  monthlyItemRequestLimit: 0,
  itemRequestUsage: FALLBACK_ITEM_REQUEST_USAGE
}

// TODO:  The following data should be cleared, it is temporary to get some data flowing while there is no up-to-date referenceOptions endpoint for Phoenix!
export const FALLBACK_PATRON_REFERENCE_OPTIONS: PatronReferenceOptions = {
  branches: [
    {
      ...FALLBACK_BRANCH,
      id: 1,
      name: 'Branch A'
    },
    {
      ...FALLBACK_BRANCH,
      id: 2,
      name: 'Branch B'
    },
    {
      ...FALLBACK_BRANCH,
      id: 3,
      name: 'Branch C'
    }
  ],
  patronCategories: [],
  pronouns: [
    {
      id: 1,
      pronounsString: 'He/Him'
    },
    {
      id: 2,
      pronounsString: 'She/Her'
    },
    {
      id: 3,
      pronounsString: 'They/Them'
    }
  ],
  addressTypes: []
};

export const FALLBACK_PATRON_ADDRESS_TYPE = {
  id: 0,
  description: '',
}

export const FALLBACK_PATRON_ADDRESS = {
  id: 0,
  patronId: 0,
  addressTypeId: 0,
  addressType: FALLBACK_PATRON_ADDRESS_TYPE,
  address: '',
  address2: '',
  city: '',
  state: undefined as unknown as State,
  zipCode: '',
  country: '',
  verified: false,
  inDistrict: false,
  primary: false,
}

export const FALLBACK_PATRON_NOTIFICATION_PREFERENCES: PatronNotificationPreferences = {
  notifications: [],
  phoneSms: '',
  notificationEmail: '',
  smsTermsAccepted: false
}

export const FALLBACK_NOTIFICATION_PREFERENCE: NotificationPreference = {
  id: 0,
  patronId: 0,
  notificationType: 'ACCOUNT',
  sendNotification: false,
  notifyEmail: false,
  notifyText: false,
  notifyPhone: false,
  active: false,
  created: '',
  modified: ''
};

export const FALLBACK_NOTIFICATION_PREFERENCES_EDIT_VALUE: EditNotificationPreferencesValue = {
  phoneSms: '',
  notificationEmail: '',
  accountNotifications: 'Email',
  renewNotifications: 'Email',
  expirationNotifications: 'Email',
  receiptNotifications: 'Email',
  holdNotifications: 'Email',
  noticeNotifications: 'Email',
  itemRequestNotifications: 'Email',
  smsTermsAccepted: false
};

export const FALLBACK_RELATED_PATRON: RelatedPatron = {
  firstName: '',
  lastName: '',
  patronId: 0,
  patronType: '',
  patronTypeBalanceLimit: 0,
  blockCount: 0,
  itemsOutCount: 0,
  overdueCount: 0,
  activeHoldCount: 0,
  readyForPickupCount: 0,
  accountBalance: 0,
  accountExpiration: ''
};

export const FALLBACK_PATRON_RELATIONSHIPS: PatronRelationships = {
  guardians: [],
  dependents: [],
  linkedAccounts: []
}

export const FALLBACK_PATRON_SETTINGS_QUICK_TOGGLE_VALUE: PatronSettingsQuickTogglesValue = {
  optInReadingHistory: false,
  oneClickHoldsEnabled: false
};

export const FALLBACK_EMAIL_LOGIN_VALUE: EmailLoginValue = {
  email: '',
  password: ''
};

export const FALLBACK_CARD_LOGIN_VALUE: CardLoginValue = {
  cardNumber: '',
  pin: ''
};
